import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

interface StatesContextType {
  isLoading: boolean;
  isEmpty: boolean;
}

const StatesContext = createContext<StatesContextType | undefined>(undefined);

interface StatesProviderProps {
  isLoading?: boolean;
  isEmpty?: boolean;
  children: ReactNode;
}

function Provider({ isLoading = false, isEmpty = false, children }: StatesProviderProps) {
  return <StatesContext.Provider value={{ isLoading, isEmpty }}>{children}</StatesContext.Provider>;
}

function useStatesContext() {
  const context = useContext(StatesContext);
  if (context === undefined) {
    throw new Error('useStatesContext must be used within a States.Provider');
  }
  return context;
}

interface StateComponentProps {
  children: ReactNode;
}

function Loading({ children }: StateComponentProps) {
  const { isLoading } = useStatesContext();
  if (!isLoading) return null;
  return <>{children}</>;
}

function Empty({ children }: StateComponentProps) {
  const { isLoading, isEmpty } = useStatesContext();
  if (isLoading || !isEmpty) return null;
  return <>{children}</>;
}

function Default({ children }: StateComponentProps) {
  const { isLoading, isEmpty } = useStatesContext();
  if (isLoading || isEmpty) return null;
  return <>{children}</>;
}

export const States = {
  Provider,
  Loading,
  Empty,
  Default,
};
